/**
 * Product Utilization service.
 *
 * Translates camelCase UI payloads → snake_case repository payloads and wraps
 * everything in `IControllerPromiseResponse<T, { message }>` so callers get
 * uniform error handling.
 */

export const productUtilizationServiceImplFactory = ({
  productUtilizationRepository,
}: {
  productUtilizationRepository: IProductUtilizationRepository;
}): IProductUtilizationService => {
  const toErr = (error: unknown) => ({
    message: (error as Error)?.message ?? "Unknown product utilization error",
  });

  const getNeoConversations: IGetNeoConversationsService = async ({
    start,
    end,
    excludeInternal,
    ignoreEmpty,
    limit,
    offset,
  }) => {
    try {
      const response = await productUtilizationRepository.getNeoConversations({
        start,
        end,
        exclude_internal: excludeInternal,
        ignore_empty: ignoreEmpty,
        limit,
        offset,
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: toErr(error) };
    }
  };

  const getNeoConversation: IGetNeoConversationService = async ({
    conversationId,
  }) => {
    try {
      const response = await productUtilizationRepository.getNeoConversation({
        conversation_id: conversationId,
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: toErr(error) };
    }
  };

  const filterNeoConversations: IFilterNeoConversationsService = async ({
    question,
    conversationIds,
  }) => {
    try {
      const response =
        await productUtilizationRepository.filterNeoConversations({
          question,
          conversation_ids: conversationIds,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: toErr(error) };
    }
  };

  return {
    getNeoConversations,
    getNeoConversation,
    filterNeoConversations,
  };
};
